import {
  Box,
  Button,
  Chip,
  Tooltip,
  useTheme,
  alpha,
  IconButton,
} from '@mui/material';
import CodeEditor from './CodeEditor';
import { useEffect, useRef, useState } from 'react';
import { API_PATCH, DBRequest, useConfig } from '../../api/api';
import { tokens } from '../../global/theme/tokens';
import TemplatePreview from '../templates/TemplatePreview';
import Loader from '../special/Loader';
import TemplateVariables from './templateEditor/TemplateVariables';
import ExoDialog from '../exo/ExoDialog';
import ExoAlert from '../exo/ExoAlert';
import { useTranslation } from 'react-i18next';
import { convertMetaFieldsToTemplateVars } from '../exo/ExoDocumentPreview';
import TypeText from '../form/form-fields/TypeText';
import { replaceTemplateBlocksInString } from './templateEditor/template-blocks/replaceTemplateBlocksInString';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArticleIcon from '@mui/icons-material/Article';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Section configuration for tabs
const SECTION_CONFIG = [
  {
    key: 'content',
    labelKey: 'Content',
    icon: ArticleIcon,
    tooltip: 'Main document content',
  },
  {
    key: 'headerP1',
    labelKey: 'Header P1',
    icon: VerticalAlignTopIcon,
    tooltip: 'Header shown on first page',
  },
  {
    key: 'headerP2',
    labelKey: 'Header P2+',
    icon: VerticalAlignTopIcon,
    tooltip: 'Header shown on subsequent pages',
  },
  {
    key: 'footerP1',
    labelKey: 'Footer P1',
    icon: VerticalAlignBottomIcon,
    tooltip: 'Footer shown on first page',
  },
  {
    key: 'footerP2',
    labelKey: 'Footer P2+',
    icon: VerticalAlignBottomIcon,
    tooltip: 'Footer shown on subsequent pages',
  },
];

const TemplateEditor = ({
  templateData,
  onSave = () => {},
  onClose = () => {},
  open = true,
  onFeedback = () => {},
  variables = [],
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const aceEditorRef = useRef(null);

  const [template, setTemplate] = useState(templateData);
  const [currentKey, setCurrentKey] = useState('content');
  const [metaData, setMetaData] = useState(templateData?.metaData || {});
  const [templateBlocks, setTemplateBlocks] = useState(null);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);
  const [showEditorUnsavedAlert, setShowEditorUnsavedAlert] = useState(false);
  const [originalTemplate, setOriginalTemplate] = useState(null);
  const [codeEditorHasUnsaved, setCodeEditorHasUnsaved] = useState(false);

  useEffect(() => {
    const initialTemplate = {
      ...templateData,
      contentCode: templateData.contentCode || templateData.content,
      headerP1Code: templateData.headerP1Code || templateData.headerP1,
      headerP2Code: templateData.headerP2Code || templateData.headerP2,
      footerP1Code: templateData.footerP1Code || templateData.footerP1,
      footerP2Code: templateData.footerP2Code || templateData.footerP2,
    };
    setTemplate(initialTemplate);
    setOriginalTemplate(initialTemplate);
    setMetaData(templateData?.metaData || {});
    if (templateData.templateType)
      DBRequest({
        config,
        path: `template-blocks?type[eq]=${templateData.templateType}`,
        onResponse: setTemplateBlocks,
      });
  }, [templateData, config]);

  // ACTIONS --------
  function handleCodeChange(data) {
    setTemplate((prev) => ({
      ...prev,
      [currentKey + 'Code']: data,
      [currentKey]: replaceTemplateBlocksInString(data, templateBlocks?.data),
    }));
  }

  useEffect(() => {
    if (templateBlocks)
      setTemplate({
        ...template,
        content: replaceTemplateBlocksInString(
          template.contentCode,
          templateBlocks?.data
        ),
        headerP1: replaceTemplateBlocksInString(
          template.headerP1Code,
          templateBlocks?.data
        ),
        headerP2: replaceTemplateBlocksInString(
          template.headerP2Code,
          templateBlocks?.data
        ),
        footerP1: replaceTemplateBlocksInString(
          template.footerP1Code,
          templateBlocks?.data
        ),
        footerP2: replaceTemplateBlocksInString(
          template.footerP2Code,
          templateBlocks?.data
        ),
      });
  }, [templateBlocks]);

  function handleHeightChange(page, headerHeight, footerHeight) {
    var newStyle = JSON.parse(template.styleJson);

    if (
      newStyle.page['headerP' + page + 'Height'] === headerHeight &&
      newStyle.page['footerP' + page + 'Height'] === footerHeight
    )
      return;
    newStyle.page['headerP' + page + 'Height'] = headerHeight;
    newStyle.page['footerP' + page + 'Height'] = footerHeight;

    setTemplate({
      ...template,
      styleJson: JSON.stringify(newStyle),
    });
  }

  function handleSectionSelect(section) {
    setCurrentKey(section);
  }

  const handleClickVariable = (textToInsert) => {
    const currentPos = aceEditorRef.current.editor.getCursorPosition();
    const text = aceEditorRef.current.editor.getValue();
    const beforeText = text.substring(
      0,
      aceEditorRef.current.editor.session.doc.positionToIndex(currentPos)
    );
    const afterText = text.substring(
      aceEditorRef.current.editor.session.doc.positionToIndex(currentPos),
      text.length
    );

    aceEditorRef.current.editor.setValue(beforeText + textToInsert + afterText);
    aceEditorRef.current.editor.clearSelection();
    aceEditorRef.current.editor.moveCursorToPosition(currentPos);
  };

  function handleSave() {
    // Check if CodeEditor has unsaved changes (not synced via Ctrl+S)
    if (codeEditorHasUnsaved) {
      setShowEditorUnsavedAlert(true);
      return;
    }

    performSave();
  }

  // Sync code editor changes and then save
  function handleSyncAndSave() {
    // Get current code from editor and sync it
    if (aceEditorRef.current?.editor) {
      const currentCode = aceEditorRef.current.editor.getValue();
      const updatedTemplate = {
        ...template,
        [currentKey + 'Code']: currentCode,
        [currentKey]: replaceTemplateBlocksInString(
          currentCode,
          templateBlocks?.data
        ),
      };
      setTemplate(updatedTemplate);
      setShowEditorUnsavedAlert(false);

      // Save with updated template
      DBRequest({
        config,
        path: 'templates/' + updatedTemplate.id,
        method: API_PATCH({ ...updatedTemplate, metaData }),
        onResponse: () => onSave({ ...updatedTemplate, metaData }),
        onFeedback: onFeedback,
      });
      setCurrentKey('content');
      onClose();
    }
  }

  function performSave() {
    DBRequest({
      config,
      path: 'templates/' + template.id,
      method: API_PATCH({ ...template, metaData }),
      onResponse: () => onSave({ ...template, metaData }),
      onFeedback: onFeedback,
    });
    setCurrentKey('content');
    onClose();
  }

  const metaTemplateVars = convertMetaFieldsToTemplateVars(
    metaData?.metaFields,
    true
  );
  const templateVars = template ? template?.vars?.concat(metaTemplateVars) : [];

  const [disableTemplateRendering, setDisableTemplateRendering] =
    useState(false);

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!originalTemplate) return false;

    // Compare code fields
    const codeFields = [
      'contentCode',
      'headerP1Code',
      'headerP2Code',
      'footerP1Code',
      'footerP2Code',
      'name',
      'isHidden',
    ];
    for (const field of codeFields) {
      if (template[field] !== originalTemplate[field]) {
        return true;
      }
    }

    // Compare metaData
    if (
      JSON.stringify(metaData) !== JSON.stringify(templateData?.metaData || {})
    ) {
      return true;
    }

    return false;
  };

  // Handle close with unsaved changes check (both template state and code editor)
  const handleClose = () => {
    if (hasUnsavedChanges() || codeEditorHasUnsaved) {
      setShowUnsavedAlert(true);
    } else {
      setCurrentKey('content');
      onClose();
    }
  };

  // Force close without saving
  const handleForceClose = () => {
    setShowUnsavedAlert(false);
    setCurrentKey('content');
    onClose();
  };

  const currentSectionIndex = SECTION_CONFIG.findIndex(
    (s) => s.key === currentKey
  );

  return (
    <ExoDialog open={open} noBlur className="pl-0 pr-0 pt-0 pb-0">
      <Box
        className="flex flex-col h-full w-full"
        sx={{
          backgroundColor: alpha(colors.bgInput, 0.95),
          backdropFilter: 'blur(20px)',
        }}
      >
        {!template.id || !templateBlocks ? (
          <Loader active={true} message={t('Loading Template...')} />
        ) : (
          <Box className="flex h-full w-full overflow-hidden">
            {/* Main Content Area */}
            <Box className="flex flex-col flex-1 overflow-hidden">
              {/* Header Section - Clean & Minimal */}
              <Box
                className="flex items-center justify-between px-4 py-3 gap-4"
                sx={{
                  backgroundColor: colors.glass,
                  backdropFilter: 'blur(12px)',
                  borderBottom: `1px solid ${alpha(colors.card, 0.3)}`,
                }}
              >
                {/* Left: Template Name */}
                <Box className="flex-1" sx={{ maxWidth: '350px' }}>
                  <TypeText
                    size="small"
                    field={{ label: t('Template Name'), key: 'name' }}
                    dataCollection={template}
                    updateValidationOnChange={(key, value) =>
                      setTemplate((prev) => ({ ...prev, [key]: value }))
                    }
                  />
                </Box>

                {/* Center: Section Tabs - Modern Pill Style */}
                <Box
                  sx={{
                    backgroundColor: colors.bgInput,
                    borderRadius: '12px',
                    padding: '4px',
                    display: 'flex',
                    gap: '2px',
                  }}
                >
                  {SECTION_CONFIG.map((section, index) => {
                    const IconComponent = section.icon;
                    const isActive = currentSectionIndex === index;
                    return (
                      <Tooltip
                        key={section.key}
                        title={t(section.tooltip)}
                        arrow
                      >
                        <Button
                          size="small"
                          onClick={() => setCurrentKey(section.key)}
                          startIcon={<IconComponent sx={{ fontSize: 16 }} />}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: isActive ? 600 : 400,
                            textTransform: 'none',
                            color: isActive ? 'inherit' : 'text.secondary',
                            backgroundColor: isActive
                              ? colors.card
                              : 'transparent',
                            boxShadow: isActive
                              ? `0 2px 8px ${alpha('#000', 0.15)}`
                              : 'none',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: isActive
                                ? colors.card
                                : alpha(colors.card, 0.5),
                            },
                          }}
                        >
                          {t(section.labelKey)}
                        </Button>
                      </Tooltip>
                    );
                  })}
                </Box>

                {/* Right: Toggle Buttons - Icon Style */}
                <Box className="flex items-center gap-1">
                  <Tooltip title={t('Toggle template visibility')} arrow>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setTemplate((prev) => ({
                          ...prev,
                          isHidden: !prev.isHidden,
                        }))
                      }
                      sx={{
                        backgroundColor: template.isHidden
                          ? alpha(colors.warning, 0.15)
                          : alpha(colors.success, 0.15),
                        color: template.isHidden
                          ? colors.warning
                          : colors.success,
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: template.isHidden
                            ? alpha(colors.warning, 0.25)
                            : alpha(colors.success, 0.25),
                        },
                      }}
                    >
                      {template.isHidden ? (
                        <VisibilityOffIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <VisibilityIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Tooltip>

                  <Tooltip
                    title={
                      metaData.useOrderIneffective
                        ? t('Order Ineffective + Order Effective')
                        : t('Order Effective Only')
                    }
                    arrow
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        setMetaData({
                          ...metaData,
                          useOrderIneffective: !metaData.useOrderIneffective,
                        })
                      }
                      sx={{
                        backgroundColor: metaData?.useOrderIneffective
                          ? alpha(colors.warning, 0.15)
                          : alpha(colors.info, 0.15),
                        color: metaData?.useOrderIneffective
                          ? colors.warning
                          : colors.info,
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: metaData?.useOrderIneffective
                            ? alpha(colors.warning, 0.25)
                            : alpha(colors.info, 0.25),
                        },
                      }}
                    >
                      <FilterNoneIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip
                    title={
                      metaData.isHoaiTemplate
                        ? t('Is HOAI Template')
                        : t('IS NOT HOAI Template')
                    }
                    arrow
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        setMetaData({
                          ...metaData,
                          isHoaiTemplate: !metaData.isHoaiTemplate,
                        })
                      }
                      sx={{
                        backgroundColor: metaData?.isHoaiTemplate
                          ? alpha(colors.warning, 0.15)
                          : alpha(colors.info, 0.15),
                        color: metaData?.isHoaiTemplate
                          ? colors.warning
                          : colors.info,
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: metaData?.isHoaiTemplate
                            ? alpha(colors.warning, 0.25)
                            : alpha(colors.info, 0.25),
                        },
                        fontSize: '12px',
                        fontWeight: 700,
                      }}
                    >
                      HOAI
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Main 3-Panel Layout */}
              <Box className="flex flex-row flex-1 overflow-hidden gap-3 p-3">
                {/* Left: Variables Panel */}
                <TemplateVariables
                  variables={template.vars}
                  onClick={handleClickVariable}
                  metaData={metaData}
                  onMetaDataChange={setMetaData}
                  templateBlocks={templateBlocks}
                  setTemplateBlocks={setTemplateBlocks}
                  templateType={template.templateType}
                />

                {/* Center: Code Editor */}
                <Box
                  className="flex flex-col flex-1 h-full overflow-hidden"
                  sx={{
                    borderRadius: '12px',
                    border: `1px solid ${alpha(colors.card, 0.3)}`,
                    overflow: 'hidden',
                  }}
                >
                  <CodeEditor
                    className="w-full h-full"
                    editorRef={aceEditorRef}
                    data={template[currentKey + 'Code'] || template[currentKey]}
                    currentKey={currentKey}
                    setData={handleCodeChange}
                    variables={templateVars}
                    disableTemplateRendering={disableTemplateRendering}
                    changeDisableTemplateRendering={setDisableTemplateRendering}
                    templateBlocks={templateBlocks}
                    onUnsavedStatusChange={setCodeEditorHasUnsaved}
                  />
                </Box>
              </Box>

              {/* Footer: Action Buttons - Floating Style */}
              <Box
                className="flex justify-between items-center px-4 py-3"
                sx={{
                  backgroundColor: colors.glass,
                  backdropFilter: 'blur(12px)',
                  borderTop: `1px solid ${alpha(colors.card, 0.3)}`,
                }}
              >
                <Button
                  onClick={handleClose}
                  startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    px: 2.5,
                    py: 1,
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: 500,
                    textTransform: 'none',
                    color: colors.error,
                    backgroundColor: alpha(colors.error, 0.1),
                    border: `1px solid ${alpha(colors.error, 0.3)}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha(colors.error, 0.2),
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  onClick={handleSave}
                  startIcon={<SaveIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#fff',
                    backgroundColor: colors.success,
                    boxShadow: `0 4px 14px ${alpha(colors.success, 0.4)}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: colors.success,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 20px ${alpha(colors.success, 0.5)}`,
                    },
                  }}
                >
                  {t('Save Template')}
                </Button>
              </Box>
            </Box>

            {/* Right: Preview Panel - Glass Effect */}
            <Box
              className="flex flex-col h-full"
              sx={{
                width: '38%',
                minWidth: '380px',
                maxWidth: '650px',
                flexShrink: 0,
                backgroundColor: colors.glass,
                backdropFilter: 'blur(12px)',
                borderLeft: `1px solid ${alpha(colors.card, 0.3)}`,
              }}
            >
              <Box
                className="flex items-center justify-between px-4 py-3"
                sx={{
                  borderBottom: `1px solid ${alpha(colors.card, 0.2)}`,
                }}
              >
                <Box className="flex items-center gap-2">
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: colors.success,
                      boxShadow: `0 0 8px ${colors.success}`,
                    }}
                  />
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      letterSpacing: '0.3px',
                    }}
                  >
                    {t('Live Preview')}
                  </span>
                </Box>
                <Chip
                  size="small"
                  icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                  label={t(
                    SECTION_CONFIG.find((s) => s.key === currentKey)
                      ?.labelKey || currentKey
                  )}
                  sx={{
                    backgroundColor: alpha(colors.info, 0.15),
                    color: colors.info,
                    fontSize: '11px',
                    fontWeight: 500,
                    height: '24px',
                    '& .MuiChip-icon': { color: colors.info },
                  }}
                />
              </Box>
              <Box
                className="overflow-y-auto p-3 flex w-full h-full"
                sx={{
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: alpha(colors.card, 0.4),
                    borderRadius: '3px',
                  },
                }}
              >
                <TemplatePreview
                  edit={true}
                  fit
                  onSelect={handleSectionSelect}
                  data={{ ...template, vars: templateVars }}
                  paginate={true}
                  onChange={handleHeightChange}
                  disableVariableRendering={disableTemplateRendering}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Unsaved Changes Alert - for closing */}
      <ExoAlert
        open={showUnsavedAlert}
        onClose={() => setShowUnsavedAlert(false)}
        onSubmit={handleForceClose}
        alert={{
          type: 'warning',
          message: t(
            'You have unsaved changes. Are you sure you want to close without saving?'
          ),
          cancelLabel: t('Keep Editing'),
          submitLabel: t('Discard Changes'),
        }}
      />

      {/* Unsaved Code Editor Alert - for saving */}
      <ExoAlert
        open={showEditorUnsavedAlert}
        onClose={() => setShowEditorUnsavedAlert(false)}
        onSubmit={handleSyncAndSave}
        alert={{
          type: 'warning',
          message: t(
            'You have unsaved changes in the code editor. Do you want to sync and save all changes?'
          ),
          cancelLabel: t('Cancel'),
          submitLabel: t('Sync & Save'),
        }}
      />
    </ExoDialog>
  );
};

export default TemplateEditor;
